
import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { api, ApiError } from "@/lib/api"
import { PUBLIC_PATHS } from "@/lib/routes"
import { fetchDisplayConfigs } from "@/lib/location-display-config"
import { fetchExpiryDateFormat } from "@/hooks/use-expiry-date-format"

/**
 * True when a JWT's `exp` claim is in the past. Conservative: a token we can't
 * parse, or one with no `exp`, is treated as NOT expired (let the server decide)
 * so a decode quirk never logs out a valid session.
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split(".")[1]
    if (!payload) return false
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: number }
    if (typeof decoded.exp !== "number") return false
    return decoded.exp * 1000 <= Date.now()
  } catch {
    return false
  }
}

// Warehouse context for location-based filtering
export interface WarehouseContext {
  warehouseId: string
  warehouseCode: string
  warehouseName: string
  facilityId: string
  facilityCode: string
  facilityName: string
}

// Facility context for facility-level filtering
export interface FacilityContext {
  facilityId: string
  facilityCode: string
  facilityName: string
}

interface User {
  id: string
  email: string
  fullName: string | null
  username?: string
  isSystemAdmin: boolean
  organizationId?: string
  organizationName?: string
  roleId?: string
  roleName?: string
  permissions?: { web: string[]; mobile: string[] }
  // Warehouse context
  defaultWarehouseId?: string        // User's assigned warehouse (null = admin, all access)
  defaultWarehouse?: WarehouseContext // Full warehouse details
  canSwitchWarehouse: boolean        // Based on role - admins can switch, operators cannot
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (token: string, user: User) => void
  logout: () => void
  refreshUser: () => Promise<void>
  // Permissions
  hasPermission: (slug: string) => boolean
  hasAnyPermission: (...slugs: string[]) => boolean
  // Facility context (enterprise feature)
  activeFacility: FacilityContext | null      // Currently active facility (null = all facilities)
  availableFacilities: FacilityContext[]      // All facilities user can access
  setActiveFacility: (facility: FacilityContext | null) => void
  // Warehouse context
  activeWarehouse: WarehouseContext | null    // Currently active warehouse (for filtering)
  availableWarehouses: WarehouseContext[]     // All warehouses user can access
  setActiveWarehouse: (warehouse: WarehouseContext | null) => void
  loadAvailableWarehouses: () => Promise<void>
  // Computed: warehouses filtered by active facility
  filteredWarehouses: WarehouseContext[]
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_KEY = "auth_token"
const USER_KEY = "auth_user"
const ACTIVE_WAREHOUSE_KEY = "active_warehouse"
const ACTIVE_FACILITY_KEY = "active_facility"

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeWarehouse, setActiveWarehouseState] = useState<WarehouseContext | null>(() => {
    if (typeof window === 'undefined') return null
    try {
      const stored = localStorage.getItem(ACTIVE_WAREHOUSE_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  })
  const [activeFacility, setActiveFacilityState] = useState<FacilityContext | null>(() => {
    if (typeof window === 'undefined') return null
    try {
      const stored = localStorage.getItem(ACTIVE_FACILITY_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  })
  const [availableWarehouses, setAvailableWarehouses] = useState<WarehouseContext[]>([])
  const [warehousesLoaded, setWarehousesLoaded] = useState(false)
  const navigate = useNavigate()
  const pathname = useLocation().pathname

  // Derive available facilities from warehouses
  const availableFacilities: FacilityContext[] = useMemo(() => {
    const facilityMap = new Map<string, FacilityContext>()
    availableWarehouses.forEach(wh => {
      if (wh.facilityId && !facilityMap.has(wh.facilityId)) {
        facilityMap.set(wh.facilityId, {
          facilityId: wh.facilityId,
          facilityCode: wh.facilityCode,
          facilityName: wh.facilityName,
        })
      }
    })
    return Array.from(facilityMap.values()).sort((a, b) => 
      a.facilityCode.localeCompare(b.facilityCode)
    )
  }, [availableWarehouses])

  // Filter warehouses by active facility
  const filteredWarehouses: WarehouseContext[] = useMemo(() => {
    if (!activeFacility) return availableWarehouses
    return availableWarehouses.filter(wh => wh.facilityId === activeFacility.facilityId)
  }, [availableWarehouses, activeFacility])

  // Auto-select warehouse when there's only one option
  useEffect(() => {
    // Skip if already have a warehouse selected
    if (activeWarehouse) return
    // Skip if no warehouses available
    if (filteredWarehouses.length === 0) return

    // Auto-select if only one warehouse available
    if (filteredWarehouses.length === 1) {
      const singleWarehouse = filteredWarehouses[0]
      setActiveWarehouseState(singleWarehouse)
      localStorage.setItem(ACTIVE_WAREHOUSE_KEY, JSON.stringify(singleWarehouse))

      // Also set facility if not already set
      if (!activeFacility) {
        const facilityContext: FacilityContext = {
          facilityId: singleWarehouse.facilityId,
          facilityCode: singleWarehouse.facilityCode,
          facilityName: singleWarehouse.facilityName,
        }
        setActiveFacilityState(facilityContext)
        localStorage.setItem(ACTIVE_FACILITY_KEY, JSON.stringify(facilityContext))
      }
    }
  }, [filteredWarehouses, activeWarehouse, activeFacility])

  // Permission helpers
  const hasPermission = useCallback((slug: string) => {
    if (!user) return false
    if (user.isSystemAdmin) return true
    return user.permissions?.web?.includes(slug) ?? false
  }, [user])

  const hasAnyPermission = useCallback((...slugs: string[]) => {
    return slugs.some(slug => hasPermission(slug))
  }, [hasPermission])

  // Load available warehouses for switching
  const loadAvailableWarehouses = useCallback(async () => {
    try {
      const warehouses = await api.getAvailableWarehouses()
      setAvailableWarehouses(warehouses || [])
    } catch (error) {
      setAvailableWarehouses([])
    } finally {
      setWarehousesLoaded(true)
    }
  }, [])

  // Clear stale warehouse/facility when they no longer exist in availableWarehouses
  useEffect(() => {
    // Only run after warehouses have been fetched from the API
    // Skip for users who can't switch (they're locked to their default)
    if (!warehousesLoaded || !user?.canSwitchWarehouse) return

    // Check if active warehouse is still valid
    if (activeWarehouse) {
      const stillExists = availableWarehouses.some(
        wh => wh.warehouseId === activeWarehouse.warehouseId
      )
      if (!stillExists) {
        setActiveWarehouseState(null)
        localStorage.removeItem(ACTIVE_WAREHOUSE_KEY)
      }
    }

    // Check if active facility is still valid
    if (activeFacility) {
      const stillExists = availableWarehouses.some(
        wh => wh.facilityId === activeFacility.facilityId
      )
      if (!stillExists) {
        setActiveFacilityState(null)
        localStorage.removeItem(ACTIVE_FACILITY_KEY)
      }
    }
  }, [availableWarehouses, activeWarehouse, activeFacility, user?.canSwitchWarehouse, warehousesLoaded])

  // Set active facility with persistence and cascade to warehouse
  const setActiveFacility = useCallback((facility: FacilityContext | null) => {
    setActiveFacilityState(facility)
    if (facility) {
      localStorage.setItem(ACTIVE_FACILITY_KEY, JSON.stringify(facility))
      // Get warehouses in this facility
      const warehousesInFacility = availableWarehouses.filter(
        wh => wh.facilityId === facility.facilityId
      )
      
      setActiveWarehouseState(prev => {
        // If current warehouse is in this facility, keep it
        if (prev && prev.facilityId === facility.facilityId) {
          return prev
        }
        // If facility has exactly 1 warehouse, auto-select it
        if (warehousesInFacility.length === 1) {
          const autoWarehouse = warehousesInFacility[0]
          localStorage.setItem(ACTIVE_WAREHOUSE_KEY, JSON.stringify(autoWarehouse))
          return autoWarehouse
        }
        // Otherwise clear warehouse selection
        localStorage.removeItem(ACTIVE_WAREHOUSE_KEY)
        return null
      })
    } else {
      localStorage.removeItem(ACTIVE_FACILITY_KEY)
    }
  }, [availableWarehouses])

  // Set active warehouse with persistence
  const setActiveWarehouse = useCallback((warehouse: WarehouseContext | null) => {
    setActiveWarehouseState(warehouse)
    if (warehouse) {
      localStorage.setItem(ACTIVE_WAREHOUSE_KEY, JSON.stringify(warehouse))
      // Also set the facility context to match
      setActiveFacilityState({
        facilityId: warehouse.facilityId,
        facilityCode: warehouse.facilityCode,
        facilityName: warehouse.facilityName,
      })
      localStorage.setItem(ACTIVE_FACILITY_KEY, JSON.stringify({
        facilityId: warehouse.facilityId,
        facilityCode: warehouse.facilityCode,
        facilityName: warehouse.facilityName,
      }))
    } else {
      localStorage.removeItem(ACTIVE_WAREHOUSE_KEY)
    }
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setUser(null)
    setActiveWarehouseState(null)
    setActiveFacilityState(null)
    setAvailableWarehouses([])
    setWarehousesLoaded(false)
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(ACTIVE_WAREHOUSE_KEY)
    localStorage.removeItem(ACTIVE_FACILITY_KEY)
    document.cookie = "has_auth=1; path=/; max-age=0"
    api.setAuthToken(null)
    // Hard redirect (not SPA navigate): guarantees a clean reset and escapes
    // any stuck error-boundary state (e.g. a 403 page), where a soft navigate
    // can fail to swap the view. Lands on a fresh /login.
    window.location.assign("/login")
  }, [])

  const login = useCallback((newToken: string, newUser: User) => {
    setToken(newToken)
    setUser(newUser)
    localStorage.setItem(TOKEN_KEY, newToken)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))
    document.cookie = "has_auth=1; path=/; max-age=31536000"
    api.setAuthToken(newToken)

    // Set warehouse and facility context from login response
    if (newUser.defaultWarehouse) {
      setActiveWarehouseState(newUser.defaultWarehouse)
      localStorage.setItem(ACTIVE_WAREHOUSE_KEY, JSON.stringify(newUser.defaultWarehouse))
      // Also set facility
      const facilityContext: FacilityContext = {
        facilityId: newUser.defaultWarehouse.facilityId,
        facilityCode: newUser.defaultWarehouse.facilityCode,
        facilityName: newUser.defaultWarehouse.facilityName,
      }
      setActiveFacilityState(facilityContext)
      localStorage.setItem(ACTIVE_FACILITY_KEY, JSON.stringify(facilityContext))
    } else {
      // Admin with no default - clear any stored preference on fresh login
      setActiveWarehouseState(null)
      setActiveFacilityState(null)
      localStorage.removeItem(ACTIVE_WAREHOUSE_KEY)
      localStorage.removeItem(ACTIVE_FACILITY_KEY)
    }

    // Always load the warehouse list — it supplies warehouse/facility
    // metadata (e.g. names for the location tree) even for single-warehouse
    // users; canSwitchWarehouse only controls the switcher UI. Gating this
    // on the flag starved pages of context and left them stuck or showing
    // "Unassigned" groupings.
    loadAvailableWarehouses()
  }, [loadAvailableWarehouses])

  // Refresh user data from API
  const refreshUser = useCallback(async () => {
    const currentToken = token || localStorage.getItem(TOKEN_KEY)
    if (!currentToken) return

    try {
      // Temporarily set token if not already set
      if (!token) {
        api.setAuthToken(currentToken)
      }
      
      const userData = await api.getCurrentUser()
      setUser(userData)
      setToken(currentToken)
      localStorage.setItem(TOKEN_KEY, currentToken)
      localStorage.setItem(USER_KEY, JSON.stringify(userData))
      api.setAuthToken(currentToken)

      // Set active warehouse and facility based on user's permissions and preferences
      const storedWarehouse = localStorage.getItem(ACTIVE_WAREHOUSE_KEY)
      const storedFacility = localStorage.getItem(ACTIVE_FACILITY_KEY)
      
      if (!userData.canSwitchWarehouse && userData.defaultWarehouse) {
        // Operator: locked to their assigned warehouse and facility
        setActiveWarehouseState(userData.defaultWarehouse)
        setActiveFacilityState({
          facilityId: userData.defaultWarehouse.facilityId,
          facilityCode: userData.defaultWarehouse.facilityCode,
          facilityName: userData.defaultWarehouse.facilityName,
        })
        localStorage.setItem(ACTIVE_WAREHOUSE_KEY, JSON.stringify(userData.defaultWarehouse))
        localStorage.setItem(ACTIVE_FACILITY_KEY, JSON.stringify({
          facilityId: userData.defaultWarehouse.facilityId,
          facilityCode: userData.defaultWarehouse.facilityCode,
          facilityName: userData.defaultWarehouse.facilityName,
        }))
      } else if (userData.canSwitchWarehouse) {
        // Admin/Manager: can switch, respect their stored preference
        // Restore facility
        if (storedFacility) {
          try {
            setActiveFacilityState(JSON.parse(storedFacility))
          } catch {
            setActiveFacilityState(null)
          }
        } else if (userData.defaultWarehouse) {
          setActiveFacilityState({
            facilityId: userData.defaultWarehouse.facilityId,
            facilityCode: userData.defaultWarehouse.facilityCode,
            facilityName: userData.defaultWarehouse.facilityName,
          })
        } else {
          setActiveFacilityState(null)
        }
        
        // Restore warehouse
        if (storedWarehouse) {
          try {
            setActiveWarehouseState(JSON.parse(storedWarehouse))
          } catch {
            // Invalid stored warehouse - fall back to default or null
            setActiveWarehouseState(userData.defaultWarehouse || null)
          }
        } else if (userData.defaultWarehouse) {
          // No stored preference, use default
          setActiveWarehouseState(userData.defaultWarehouse)
          localStorage.setItem(ACTIVE_WAREHOUSE_KEY, JSON.stringify(userData.defaultWarehouse))
        } else {
          // No default - show all (null = no filter)
          setActiveWarehouseState(null)
        }
      } else {
        // Edge case: can't switch but no default (shouldn't happen)
        setActiveWarehouseState(null)
        setActiveFacilityState(null)
      }

      // Always load the warehouse list (see login()) — metadata for every
      // user, not just those who can switch.
      loadAvailableWarehouses()
    } catch (error: unknown) {
      // Only logout on auth errors (401/403), not network errors
      // Network errors should keep user logged in with cached data
      const isAuthError = error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      if (isAuthError) {
        logout()
      }
      // For network errors, user stays logged in with cached data
    }
  }, [token, loadAvailableWarehouses, logout])

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedUser = localStorage.getItem(USER_KEY)
    const storedWarehouse = localStorage.getItem(ACTIVE_WAREHOUSE_KEY)
    const storedFacility = localStorage.getItem(ACTIVE_FACILITY_KEY)

    if (storedToken && storedUser && !isTokenExpired(storedToken)) {
      try {
        const parsedUser = JSON.parse(storedUser)
        setToken(storedToken)
        setUser(parsedUser)
        // Sync auth hint cookie for middleware
        document.cookie = "has_auth=1; path=/; max-age=31536000"
        // Set token in API client
        api.setAuthToken(storedToken)

        // Restore facility context
        if (storedFacility) {
          try {
            setActiveFacilityState(JSON.parse(storedFacility))
          } catch {
            // Invalid stored facility
          }
        } else if (parsedUser.defaultWarehouse) {
          setActiveFacilityState({
            facilityId: parsedUser.defaultWarehouse.facilityId,
            facilityCode: parsedUser.defaultWarehouse.facilityCode,
            facilityName: parsedUser.defaultWarehouse.facilityName,
          })
        }

        // Restore warehouse context
        if (storedWarehouse) {
          try {
            setActiveWarehouseState(JSON.parse(storedWarehouse))
          } catch {
            // Invalid stored warehouse
          }
        } else if (parsedUser.defaultWarehouse) {
          setActiveWarehouseState(parsedUser.defaultWarehouse)
        }
        
        // Defer background sync so page-critical API calls (e.g. stock list) run first
        setTimeout(() => {
          refreshUser().catch(() => {})
          fetchDisplayConfigs().catch(() => {})
          fetchExpiryDateFormat().catch(() => {})
        }, 3000)
      } catch (error) {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
        localStorage.removeItem(ACTIVE_WAREHOUSE_KEY)
        localStorage.removeItem(ACTIVE_FACILITY_KEY)
        document.cookie = "has_auth=1; path=/; max-age=0"
      }
    } else {
      // No token, or an EXPIRED token: clear any stale session so the route
      // guard redirects to /login instead of leaving the user on a dead page.
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem(ACTIVE_WAREHOUSE_KEY)
      localStorage.removeItem(ACTIVE_FACILITY_KEY)
      document.cookie = "has_auth=1; path=/; max-age=0"
      api.setAuthToken(null)
    }

    setIsLoading(false)
  }, [])

  const isAuthenticated = !!token && !!user

  // Listen for 401 from api.ts and trigger logout immediately
  useEffect(() => {
    const handleUnauthorized = () => { if (token) logout() }
    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [token, logout])

  // The home launcher makes no API calls, so an idle expiry won't trigger a 401.
  // Re-check the token when the tab regains focus and log out if it has expired.
  useEffect(() => {
    const checkExpiry = () => { if (token && isTokenExpired(token)) logout() }
    window.addEventListener('focus', checkExpiry)
    document.addEventListener('visibilitychange', checkExpiry)
    return () => {
      window.removeEventListener('focus', checkExpiry)
      document.removeEventListener('visibilitychange', checkExpiry)
    }
  }, [token, logout])

  // Protect routes (except login, register, and password reset)
  // Note: This runs after initial render, so layout.tsx handles the initial blocking
  useEffect(() => {
    if (isLoading) return

    const isPublicRoute = PUBLIC_PATHS.some(route => pathname.startsWith(route))

    if (!isAuthenticated && !isPublicRoute) {
      navigate("/login", { replace: true })
    }
    // System admins are allowed on WMS routes too — no forced redirect to
    // /admin. They can navigate there explicitly when they want admin features.
  }, [isLoading, isAuthenticated, pathname, navigate, user])

  const contextValue = useMemo(() => ({
    user, token, isAuthenticated, isLoading,
    login, logout, refreshUser,
    hasPermission, hasAnyPermission,
    activeFacility, availableFacilities, setActiveFacility,
    activeWarehouse, availableWarehouses, setActiveWarehouse,
    loadAvailableWarehouses, filteredWarehouses,
  }), [
    user, token, isAuthenticated, isLoading,
    login, logout, refreshUser,
    hasPermission, hasAnyPermission,
    activeFacility, availableFacilities, setActiveFacility,
    activeWarehouse, availableWarehouses, setActiveWarehouse,
    loadAvailableWarehouses, filteredWarehouses,
  ])

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
